<template>
  <header class="header">
    <nav class="navbar navbar-expand-lg navbar-light fixed-top shadow-sm">
      <div class="container-fluid">
        <router-link to="/" class="navbar-brand">
          <img :src="companyLogo" alt="EKU MARKET Logo" class="brand-logo" />
        </router-link>

        <button
          class="navbar-toggler"
          type="button"
          data-bs-toggle="collapse"
          data-bs-target="#navbarNav"
          aria-controls="navbarNav"
          aria-expanded="false"
          aria-label="Toggle navigation"
          @click="isMenuOpen = !isMenuOpen"
        >
          <span v-if="isMenuOpen">X</span>
          <span v-else><i class="fas fa-bars"></i></span>
        </button>

        <div class="collapse navbar-collapse" :class="{ show: isMenuOpen }" id="navbarNav">
          <ul class="navbar-nav ms-auto mb-2 mb-lg-0">
            <li class="nav-item">
              <router-link to="/" class="nav-link" @click="closeMenu">Home</router-link>
            </li>

            <li class="nav-item">
              <router-link to="/product" class="nav-link" @click="closeMenu">Shop</router-link>
            </li>
            
            <li class="nav-item">
              <router-link to="/order" class="nav-link" @click="closeMenu">
                <i class="fas fa-box"></i>
                <span v-if="authStore.orderCount > 0" class="cart-badge">{{ authStore.orderCount }}</span>
                <span v-else class="cart-badge">0</span>
              </router-link>
            </li>
            
            <li class="nav-item">
              <router-link to="/cart" class="nav-link" @click="closeMenu">
                <i class="fas fa-shopping-cart"></i>
                <span v-if="authStore.cartCount > 0" class="cart-badge">{{ authStore.cartCount }}</span>
                <span v-else class="cart-badge">0</span>
              </router-link>
            </li>
            
            <li v-if="!authStore.isAuthenticated" class="nav-item">
              <router-link to="/login" class="nav-link" @click="closeMenu">Login</router-link>
            </li>
            
            <!-- User Dropdown for Authenticated Users -->
            <li v-if="authStore.isAuthenticated" class="nav-item dropdown-user" @click.stop>
              <button class="nav-link user-dropdown-btn" @click="toggleDropdown">
                <div class="user-avatar-mini">
                  <i class="fas fa-user-circle"></i>
                </div>
                <span class="username">{{ authStore.user?.name || 'Account' }}</span>
                <i class="fas fa-chevron-down" :class="{ 'rotated': isDropdownOpen }"></i>
              </button>
              
              <!-- Dropdown Menu -->
              <transition name="dropdown-fade">
                <div v-if="isDropdownOpen" class="user-dropdown-menu">
                  <div class="dropdown-header">
                    <div class="dropdown-avatar">
                      <i class="fas fa-user-circle"></i>
                    </div>
                    <div class="dropdown-user-info">
                      <p class="dropdown-name">{{ authStore.userDisplayName }}</p>
                      <p class="dropdown-email">{{ authStore.user?.email || 'customer@ekumarket.com' }}</p>
                    </div>
                  </div>
                  
                  <div class="dropdown-divider"></div>
                  
                  <router-link 
                  v-if="authStore.isAuthenticated && (authStore.isAdmin || authStore.isAgent || authStore.isStaff)"
                    to="/dashboard" class="dropdown-item" @click="closeDropdownAndMenu">
                    <i class="fas fa-tachometer-alt"></i>
                    <span>Dashboard</span>
                  </router-link>


                  
                  <router-link to="/profile" class="dropdown-item" @click="closeDropdownAndMenu">
                    <i class="fas fa-user"></i>
                    <span>My Profile</span>
                  </router-link>
                  
                  <router-link to="/profile?tab=orders" class="dropdown-item" @click="closeDropdownAndMenu">
                    <i class="fas fa-shopping-bag"></i>
                    <span>My Orders</span>
                    <span class="dropdown-badge">{{ authStore.orderCount || 0 }}</span>
                  </router-link>
                  
                  <router-link to="/profile?tab=token" class="dropdown-item" @click="closeDropdownAndMenu">
                    <i class="fas fa-coins"></i>
                    <span>EKU Tokens</span>
                    <span class="dropdown-badge token-badge">{{ tokenBalance }} EKU</span>
                  </router-link>
                  
                  <router-link to="/wishlist" class="dropdown-item" @click="closeDropdownAndMenu">
                    <i class="fas fa-heart"></i>
                    <span>Wishlist</span>
                  </router-link>
                  
                  <router-link to="/settings" class="dropdown-item" @click="closeDropdownAndMenu">
                    <i class="fas fa-cog"></i>
                    <span>Settings</span>
                  </router-link>
                  
                  <div class="dropdown-divider"></div>
                  
                  <button @click="handleLogout" class="dropdown-item logout-item">
                    <i class="fas fa-sign-out-alt"></i>
                    <span>Logout</span>
                  </button>
                </div>
              </transition>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  </header>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { useAuthStore } from "@/Stores/authStore";
import companyLogo from '@/assets/eku_logo.png';

const router = useRouter();
const isMenuOpen = ref(false);
const isDropdownOpen = ref(false);
const tokenBalance = ref(2450); // Replace with actual store data later

// Directly use the store - no need for local refs or onMounted
const authStore = useAuthStore();

const handleLogout = async () => {
  isDropdownOpen.value = false;
  isMenuOpen.value = false;
  authStore.ResetAuthData();
  router.push("/login");
};

const toggleDropdown = (event) => {
  event.stopPropagation();
  isDropdownOpen.value = !isDropdownOpen.value;
};

const closeDropdown = () => {
  isDropdownOpen.value = false;
};

const closeMenu = () => {
  isMenuOpen.value = false;
};

const closeDropdownAndMenu = () => {
  isDropdownOpen.value = false;
  isMenuOpen.value = false;
};

// Close dropdown when clicking outside
const handleClickOutside = (event) => {
  const dropdown = document.querySelector('.dropdown-user');
  if (dropdown && !dropdown.contains(event.target)) {
    isDropdownOpen.value = false;
  }
};

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
/* ============================================
   YOUR ORIGINAL STYLES (PRESERVED)
   ============================================ */
header {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  background-color: rgb(215, 55, 55);
}

nav {
  background-color: #ffffff;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  padding: 10px 20px;
}

.nav-item {
  margin-left: 20px;
}

.nav-link, .btn-logout {
  font-weight: bold;
  color: #2c3e50;
  text-decoration: none;
  transition: color 0.3s ease;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  padding: 8px 0;
  display: inline-block;
  font-family: inherit;
}

.nav-link:hover, .btn-logout:hover {
  color: rgb(215, 55, 55);
}

/* Specific logout color if you want it different */
.btn-logout {
  color: #dc3545;
}

.btn-logout:hover {
  color: #bb2d3b;
}

.nav-link:hover {
  color: rgb(215, 55, 55);
}

.nav-link.router-link-exact-active {
  color: rgb(215, 55, 55);
  border-bottom: 2px solid rgb(215, 55, 55);
}

.navbar-toggler-icon-close {
  display: inline-block;
  width: 1.5em;
  height: 1.5em;
  line-height: 1.5em;
  text-align: center;
  font-size: 1.2em;
}

.navbar-toggler {
  border: none;
  outline: none;
  border-radius: 0;
  box-shadow: none;
  background-color: rgb(212, 175, 55);
  color: #fff;
}

.navbar-toggler:focus {
  box-shadow: none;
}

.btn-logout {
  background: none;
  border: none;
  color: #dc3545;
  cursor: pointer;
  font-weight: bold;
}

.btn-logout:hover {
  color: #bb2d3b;
}

/* .navbar {
  background: linear-gradient(
    90deg, 
    rgba(212, 175, 55, 0),
    rgba(212, 175, 55, 0.8),
    rgba(212, 175, 55, 0)
  );
  background-size: 200% 100%;
  animation: shimmer 3s ease-in-out infinite;
} */
/* .navbar{
background-color:     rgb(216, 186, 16);
} */
.navbar{
background-color:     rgb(240, 238, 227);
}

@keyframes shimmer {
  0% {
    background-position: -100% 0;
  }
  100% {
    background-position: 100% 0;
  }
}

.cart-badge {
  background-color: red;
  color: white;
  border-radius: 50%;
  padding: 2px 6px;
  font-size: 12px;
  margin-left: 5px;
  min-width: 20px;
  display: inline-block;
  text-align: center;
}

.brand-logo {
  height: 32px;
  width: auto;
  max-width: 100%;
  vertical-align: middle;
  transition: opacity 0.2s ease;
}

.navbar-brand:hover .brand-logo {
  opacity: 0.85;
}

@media (max-width: 768px) {
  .brand-logo {
    height: 32px;
  }
}

@media (max-width: 768px) {
  .logout-text {
    display: none;
  }
}

@media (min-width: 769px) {
  .logout-text {
    margin-left: 5px;
  }
}

/* styles for dropdown menu */

/* Dropdown User Container */
.dropdown-user {
  position: relative;
}

/* User Dropdown Button */
.user-dropdown-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 30px;
  transition: all 0.3s ease;
  color: #2c3e50;
  font-weight: bold;
  font-size: 1rem;
}

.user-dropdown-btn:hover {
  background: rgba(0, 0, 0, 0.05);
  color: rgb(215, 55, 55);
}

.user-avatar-mini {
  width: 32px;
  height: 32px;
  background: linear-gradient(135deg, #D4AF37, #FFD700);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.user-avatar-mini i {
  font-size: 18px;
  color: #1a1a1a;
}

.username {
  font-size: 0.95rem;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-dropdown-btn .fa-chevron-down {
  font-size: 12px;
  color: #666;
  transition: transform 0.3s ease;
}

.user-dropdown-btn .fa-chevron-down.rotated {
  transform: rotate(180deg);
}

/* Dropdown Menu */
.user-dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 280px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  z-index: 1000;
  border: 1px solid #e0e0e0;
}

/* Dropdown Animation */
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
  transition: all 0.2s ease;
}

.dropdown-fade-enter-from,
.dropdown-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Dropdown Header */
.dropdown-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  background: #f8f9fa;
}

.dropdown-avatar {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #D4AF37, #FFD700);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.dropdown-avatar i {
  font-size: 28px;
  color: #1a1a1a;
}

.dropdown-user-info {
  flex: 1;
}

.dropdown-name {
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 4px;
  font-size: 0.95rem;
}

.dropdown-email {
  font-size: 0.75rem;
  color: #666;
  word-break: break-all;
}

/* Dropdown Items */
.dropdown-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  color: #2c3e50;
  text-decoration: none;
  transition: all 0.2s ease;
  cursor: pointer;
  background: none;
  border: none;
  width: 100%;
  text-align: left;
  font-size: 0.9rem;
  font-weight: 500;
}

.dropdown-item:hover {
  background: #f8f9fa;
  color: rgb(215, 55, 55);
}

.dropdown-item i {
  width: 20px;
  font-size: 1rem;
  color: #666;
}

.dropdown-item:hover i {
  color: rgb(215, 55, 55);
}

.dropdown-item span {
  flex: 1;
}

.dropdown-badge {
  background: #f0f0f0;
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  color: #666;
}

.token-badge {
  background: linear-gradient(135deg, #D4AF37, #FFD700);
  color: #1a1a1a;
}

.dropdown-divider {
  height: 1px;
  background: #e0e0e0;
  margin: 4px 0;
}

.logout-item {
  color: #dc3545;
}

.logout-item:hover {
  background: #fee2e2;
  color: #dc3545;
}

.logout-item:hover i {
  color: #dc3545;
}

/* Mobile Responsive */
@media (max-width: 992px) {
  .dropdown-user {
    width: 100%;
  }
  
  .user-dropdown-btn {
    width: 100%;
    justify-content: flex-start;
    padding: 10px 0;
  }
  
  .user-dropdown-menu {
    position: static;
    width: 100%;
    box-shadow: none;
    margin-top: 10px;
    border: 1px solid #e0e0e0;
  }
  
  .username {
    flex: 1;
    text-align: left;
  }
}

@media (max-width: 768px) {
  .user-avatar-mini {
    width: 28px;
    height: 28px;
  }
  
  .user-avatar-mini i {
    font-size: 16px;
  }
  
  .dropdown-name {
    font-size: 0.9rem;
  }
  
  .dropdown-email {
    font-size: 0.7rem;
  }
  
  .user-dropdown-btn {
    padding: 8px 0;
  }
}
</style>